import {
    Modal,
    useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
    ApiResponse,
    Curriculum,
    DeleteCurriculumResponse,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, IconCommunicationInformation, Text } from "@twinkltech/web-ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { paths } from "@/paths";
import { captureAppError } from "@/utils/monitoring/sentry";

import { GENERIC_CURRICULUM_FORM_NS } from "../GenericCurriculumModal/GenericCurriculumModal.i18n";

export const DeleteCurriculumModal = ({
  curriculum,
  error = null,
  loading = false,
  onClose,
  onSubmit,
  open,
  shouldRedirectOnAction = false,
}: {
  curriculum?: Curriculum | null;
  error?: Error | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (params: { id: string }) => ApiResponse<DeleteCurriculumResponse>;
  open: boolean;
  shouldRedirectOnAction?: boolean;
}) => {
  const { t } = useTranslation(GENERIC_CURRICULUM_FORM_NS);
  const { addErrorToast, addSuccessToast } = useToasts();
  const navigate = useNavigate();

  const { click } = useTwinklAnalytics();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Accessibility IDs
  const formId = useId();

  const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting || !curriculum) return;

    setIsSubmitting(true);
    const response = await onSubmit({
      id: curriculum.id,
    });
    setIsSubmitting(false);

    if (response instanceof Error) {
      captureAppError(response, {
        action: "delete",
        area: "curriculum",
        extra: {
          component: "DeleteCurriculumModal",
          curriculumId: curriculum.id,
        },
      });
      addErrorToast({
        message: "Curriculum deletion failed",
      });
      // early return on error
      return;
    }

    addSuccessToast({
      message: "Curriculum deleted successfully",
    });

    click("click_delete_modal_cta", {
      ctaCopy: "Delete",
    });

    if (shouldRedirectOnAction) {
      navigate(paths.CURRICULUM_LIST_PATH);
    }

    onClose();
  };

  if (error) {
    return (
      <Modal hasCloseButton open={open} width="md" onClose={onClose}>
        <Modal.Header>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body className="mb-2 max-h-[80vh]">
          <Text as="p" className="m-0! leading-tight" variant="base-medium">
            {error.message}
          </Text>
        </Modal.Body>
        <Modal.Footer className="gap-3">
          <Button type="button" variant="outlined" onClick={onClose}>
            {t("actions.close", { ns: COMMON_NS })}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (!curriculum) {
    return (
      <Modal hasCloseButton open={open} width="md" onClose={onClose}>
        <Modal.Header>
          <Modal.Title>No Curriculum Selected</Modal.Title>
        </Modal.Header>
        <Modal.Body className="mb-2 max-h-[80vh]">
          <Text as="p" className="m-0! leading-tight" variant="base-medium">
            Please select a curriculum to delete.
          </Text>
        </Modal.Body>
        <Modal.Footer className="gap-3">
          <Button type="button" variant="outlined" onClick={onClose}>
            {t("actions.close", { ns: COMMON_NS })}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal hasCloseButton open={open} width="md" onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Delete Curricula</Modal.Title>
        <Text className="mt-2" variant="base-normal">
          Are you sure you want to delete this curriculum?
        </Text>
      </Modal.Header>
      <Modal.Body className="mb-2 max-h-[80vh]">
        <form
          className="py-2 flex flex-col"
          id={formId}
          onSubmit={onFormSubmit}
        >
          <div className="flex flex-col items-center mt-10">
            <Text variant="2xl-medium">
              Confirm you would like to delete this curriculum:
            </Text>
            <Text className="mt-8" variant="xl-normal">
              {curriculum.displayLabel}
            </Text>
            <div className="flex items-center gap-4 bg-neutral-100 p-200 rounded-md mt-8">
              <IconCommunicationInformation className="w-10" />
              <Text as="p" className="m-0! leading-tight" variant="base-medium">
                Proceeding with this action will remove this curriculum
                permanently - if you would prefer to archive it, close this
                modal and edit the visibility to &apos;archived&apos;.
              </Text>
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-3">
        <Button
          type="button"
          variant="outlined"
          onClick={() => {
            onClose();
            click("click_delete_modal_cta", {
              ctaCopy: "Cancel",
            });
          }}
        >
          {t("actions.cancel", { ns: COMMON_NS })}
        </Button>
        <Button
          disabled={loading || !open || isSubmitting}
          form={formId}
          variant="error"
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
